<template>
    <div
        v-if="explanation || location || !dashboardConfigStore.config.shouldUseLocation"
        class="text-xs text-ink-subtle"
        data-test="prediction-explanation"
    >
        <div
            v-if="location || !dashboardConfigStore.config.shouldUseLocation"
            class="flex gap-2 items-start mb-1"
        >
            <p class="my-0">
                {{
                    dashboardConfigStore.config.shouldUseLocation
                        ? `We think you are near ${location}.`
                        : "Your location is not being used for predictions."
                }}
            </p>

            <AppIconButton
                class="appButton appButton--secondary p-0 border-none bg-inherit text-ink-muted"
                @click="shouldShowSettingsPopup = true"
                label="Location settings"
                icon="settings"
                iconClass="size-4 inline"
            />
        </div>
        <p v-if="explanation">
            {{ explanation }}
        </p>
    </div>

    <AppModal
        :isOpen="shouldShowSettingsPopup"
        closeLabel="Close settings"
        @close="shouldShowSettingsPopup = false"
    >
        <template #header>Location</template>

        <div class="px-5 py-4 flex flex-col gap-2 items-start">
            <p v-if="!dashboardConfigStore.config.shouldUseLocation">
                Your location is not being used for predictions.
            </p>
            <p v-else-if="location">We think you are near {{ location }}.</p>
            <p v-if="journeySelectionStore.currentCoordinates" class="flex gap-2 items-baseline">
                Your current coordinates are latitude
                <a
                    :href="`https://google.com/maps/search/?api=1&query=${journeySelectionStore.currentCoordinates?.latitude},${journeySelectionStore.currentCoordinates?.longitude}`"
                    target="_blank"
                    class="underline"
                >
                    <pre class="inline"
                        >{{ journeySelectionStore.currentCoordinates?.latitude }}, {{
                            journeySelectionStore.currentCoordinates?.longitude
                        }}</pre>
                </a>
            </p>
            <label class="flex items-center gap-2 text-sm">
                <input
                    type="checkbox"
                    role="switch"
                    class="size-4 accent-primary"
                    v-model="dashboardConfigStore.config.shouldUseLocation"
                    data-test="location-predictions-toggle"
                />
                Use location for predictions
            </label>
        </div>
    </AppModal>
</template>

<script setup lang="ts">
import AppIconButton from "@/components/AppIconButton.vue";
import type {JourneyPrediction} from "../../../journeys/journeyPrediction";
import {ref, computed} from "vue";
import {useDashboardConfigStore} from "@/trainDashboard/store/dashboardConfig.store.ts";
import AppModal from "@/components/Modal/AppModal.vue";
import {useJourneySelectionStore} from "@/trainDashboard/store/journeySelection.store.ts";

const props = defineProps<{
    prediction: JourneyPrediction;
    isPredicted: boolean;
}>();
const dashboardConfigStore = useDashboardConfigStore();
const journeySelectionStore = useJourneySelectionStore();

const shouldShowSettingsPopup = ref(false);

const location = computed(() => {
    const {nearbyStationGroupId} = props.prediction;
    const group = dashboardConfigStore.config.stationGroups.find(
        (group) => group.id === nearbyStationGroupId
    );

    return group ? group.name : null;
});

const explanation = computed(() => {
    const {reason} = props.prediction;

    if (!props.isPredicted || !reason) {
        return null;
    }

    if (reason.type === "nearby") {
        return `Choose a journey below.`;
    }

    if (reason.type === "saved") {
        return location.value
            ? `This is your ${reason.onlyJourney ? "only" : "first"} saved journey from ${location.value}.`
            : null;
    }

    const schedule = dashboardConfigStore.config.schedules.find(
        (schedule) => schedule.id === reason.scheduleId
    );

    if (!schedule) {
        return null;
    }

    return !location.value || reason.timing === "active"
        ? `This journey was chosen because of your “${schedule.name}” schedule.`
        : `"${schedule.name}" is your next schedule from ${location.value}.`;
});
</script>
